import React from "react";
import { useState, useEffect } from "react"
import "./OrderProgressCard.css";
import _ from "lodash";

import { ReactComponent as PendingIcon } from "../../assets/image/svg/pendingIcon.svg";
import { ReactComponent as Confirmedcon } from "../../assets/image/svg/confirmIcon.svg";
import { ReactComponent as InvoicedIcon } from "../../assets/image/svg/invoicedIcon.svg";
import { ReactComponent as OutForDeliveryIcon } from "../../assets/image/svg/OutForDeliveredIcon.svg";
import { ReactComponent as DeliveredIcon } from "../../assets/image/svg/DeliveredIcon.svg";

interface OrderTrackingProps {
  status: string;
  trackingStagesList: any[]
  stages: Record<string, number>,
  insideManageInvoice?: boolean,
}

const OrderProgressCardV2: React.FC<OrderTrackingProps> = ({
  status,
  trackingStagesList,
  stages,
  insideManageInvoice
}) => {

  const [stagesConfig, setStagesConfig] = useState<Record<string, any>>({})

  useEffect(() => {
    // compute fill value - but override for REJECTED cases
    let computedFill: number = stages?.[status] ?? -Infinity;

    if (String(status ?? "").toUpperCase() === "REJECTED") {
      if (insideManageInvoice) {
        // Rejected while in the "invoiced" context:
        // show earlier stages as "normal/filled" (set to PENDING),
        // but invoice stage will be painted red by special-casing below.
        computedFill = stages?.["PENDING"] ?? -Infinity;
      } else {
        // Rejected at PENDING: don't auto-fill anything; special-case will make PENDING red.
        computedFill = -Infinity;
      }
    }

    const updatedStagesColors = (trackingStagesList ?? []).reduce((acc: any, curr: any) => {
      const { id, dmsLabelColor, dmsColor, dmsLabel } = curr ?? {}

      // handle comma-separated ids by splitting and assigning same config to each key
      const ids: string[] = String(id ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      ids.forEach((rawId) => {
        const key = rawId.toUpperCase(); // normalize key to uppercase for consistent lookups
        const fillWithColor: boolean = (stages?.[key] ?? +Infinity) <= computedFill

        if (!acc?.[key]) {
          acc[key] = { styles: {}, label: {} };
        }

        acc[key].styles = {
          color: fillWithColor ? (dmsLabelColor ?? "#7c7c7c") : "#7c7c7c",
          borderColor: fillWithColor ? (dmsLabelColor ?? "#B9B9B9") : "#B9B9B9",
          backgroundColor: fillWithColor ? (dmsColor ?? "#fff") : "#fff",
        }

        acc[key].addInfo = { label: dmsLabel ?? rawId }
      });

      return acc
    }, {})

    setStagesConfig(updatedStagesColors)
  }, [status, trackingStagesList, stages, insideManageInvoice])


  const getIcon = (stage: string) => {
    const s = String(stage ?? "").toUpperCase();
    switch (s) {
      case "PENDING":
        return <PendingIcon />;
      case "INVOICED":
        return <InvoicedIcon />;
      case "OUT FOR DELIVERY":
        return <OutForDeliveryIcon />;
      case "DELIVERED":
        return <DeliveredIcon />;
      case "CONFIRMED":
        return <Confirmedcon />;
      default:
        return <></>;
    }
  };


  const getStageStyles = (stage: string) => {
    const stageKey = String(stage ?? "").toUpperCase();

    // keep your existing rejected special-casing (backwards-compatible)
    if (status?.toUpperCase() === "REJECTED" && stageKey === "PENDING" && !insideManageInvoice){ 
      return {
        color: "#B54747",
        borderColor: "#B54747",
        backgroundColor: "#FFF1F1"
      }
    } else if (status?.toUpperCase() === "REJECTED" && stageKey === "INVOICED" && insideManageInvoice) {
      return {
        color: "#B54747",
        borderColor: "#B54747",
        backgroundColor: "#FFF1F1"
      }
    }

    const { color, borderColor, backgroundColor } = stagesConfig?.[stageKey]?.styles ?? {}

    return {
      color,
      borderColor,
      backgroundColor
    };
  };

  const getStageLabel = (stage: string) => {
    const stageKey = String(stage ?? "").toUpperCase();
    const { label } = stagesConfig?.[stageKey]?.addInfo ?? {}
    return label ?? stage
  }


  return (
    <div className="OrderTrackingContainer">
      {Object.keys(stages).sort((a, b) => stages[a] - stages[b])?.map((stage: string, idx: number, arr: any) => {
        return (
          <div key={stage} className="OrderTracking_stageContainer">
            <div className="OrderTracking_stageNameContainer">
              <div style={getStageStyles(stage)}>{getIcon(stage)}</div>
              <span style={{ color: getStageStyles(stage)?.color }}>{_.capitalize(String(getStageLabel(stage)))}</span>
            </div>
            {idx < arr?.length - 1 && <span className="OrderTracking_stageSeperator">------</span>}
          </div>
        );
      })}
    </div>
  );
};

export default OrderProgressCardV2;

export const stagesV2: Record<string, number> = { PENDING: 1, INVOICED: 2, "OUT FOR DELIVERY": 3, DELIVERED: 4, };
